import { useEffect, useState, useCallback } from "react";
import { Trophy, Crown, Medal, RefreshCw, Loader2 } from "lucide-react";
import { gameApi } from "@/lib/gameApi";

const shortWallet = (w) => (w ? `${w.slice(0, 4)}…${w.slice(-4)}` : "—");
const fmt = (n) =>
  Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 });

export const Leaderboard = ({ currentWallet }) => {
  const [data, setData] = useState({ top_runs: [], top_earners: [] });
  const [tab, setTab] = useState("earners"); // "earners" | "runs"
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await gameApi.leaderboard(20);
      setData(r);
    } catch (e) {
      // silent
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const rows = tab === "earners" ? data.top_earners : data.top_runs;

  return (
    <section
      className="mx-auto max-w-6xl px-4 md:px-8 pb-20"
      data-testid="leaderboard-section"
    >
      <div className="flex items-end justify-between flex-wrap gap-3 mb-5">
        <div>
          <p className="font-accent text-xl text-[var(--jd-secondary)]">
            who&apos;s riding hardest
          </p>
          <h2 className="font-display font-bold text-3xl md:text-4xl uppercase leading-none mt-1 neon-title-gold">
            Leaderboard
          </h2>
        </div>
        <div className="flex items-center gap-2">
          <div className="inline-flex gg-panel rounded-xl overflow-hidden" data-testid="leaderboard-tabs">
            <button
              type="button"
              onClick={() => setTab("earners")}
              data-testid="tab-earners"
              className={`px-4 py-2 font-display uppercase text-xs transition-colors ${
                tab === "earners"
                  ? "bg-[var(--jd-secondary)] text-black"
                  : "text-[var(--jd-ocean)] hover:text-[var(--jd-text)]"
              }`}
            >
              Top Earners
            </button>
            <button
              type="button"
              onClick={() => setTab("runs")}
              data-testid="tab-runs"
              className={`px-4 py-2 font-display uppercase text-xs border-l border-white/10 transition-colors ${
                tab === "runs"
                  ? "bg-[var(--jd-primary)] text-black"
                  : "text-[var(--jd-ocean)] hover:text-[var(--jd-text)]"
              }`}
            >
              Best Runs
            </button>
          </div>
          <button
            type="button"
            onClick={load}
            disabled={loading}
            data-testid="leaderboard-refresh"
            className="gg-terminal rounded-xl p-2.5 text-[var(--jd-primary)] hover:scale-105 transition-transform"
            aria-label="Refresh leaderboard"
          >
            {loading ? (
              <Loader2 size={18} className="animate-spin" />
            ) : (
              <RefreshCw size={18} />
            )}
          </button>
        </div>
      </div>

      <div
        className="gg-panel rounded-2xl overflow-hidden"
        data-testid="leaderboard-table"
      >
        <table className="w-full text-left">
          <thead>
            <tr className="bg-black/50 text-[var(--jd-primary)] font-mono uppercase text-xs tracking-widest border-b border-[rgba(0,229,255,0.25)]">
              <th className="px-4 py-3 w-16">Rank</th>
              <th className="px-4 py-3">Wallet</th>
              {tab === "earners" ? (
                <>
                  <th className="px-4 py-3 text-right">$JETDOG</th>
                  <th className="px-4 py-3 text-right hidden md:table-cell">
                    Runs
                  </th>
                </>
              ) : (
                <>
                  <th className="px-4 py-3 text-right">Best Run</th>
                  <th className="px-4 py-3 text-right hidden md:table-cell">
                    Duration
                  </th>
                </>
              )}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td
                  colSpan={4}
                  className="px-4 py-10 text-center text-[var(--jd-ocean)]"
                  data-testid="leaderboard-empty"
                >
                  {loading
                    ? "Loading…"
                    : "No runs yet. Be the first to ride the wave."}
                </td>
              </tr>
            ) : (
              rows.map((row, i) => {
                const wallet = row.wallet;
                const isMe =
                  currentWallet && wallet && wallet === currentWallet;
                return (
                  <tr
                    key={`${tab}-${wallet}-${i}`}
                    data-testid={`leaderboard-row-${i + 1}`}
                    className={`border-t border-white/5 transition-colors ${
                      isMe ? "bg-[rgba(255,215,0,0.08)]" : "hover:bg-white/[0.03]"
                    }`}
                  >
                    <td className="px-4 py-3">
                      <RankBadge rank={i + 1} />
                    </td>
                    <td className="px-4 py-3 font-mono text-sm text-[var(--jd-primary)]">
                      {shortWallet(wallet)}
                      {isMe && (
                        <span className="ml-2 font-display uppercase text-[10px] bg-[var(--jd-accent)] text-white px-2 py-0.5 rounded">
                          you
                        </span>
                      )}
                    </td>
                    {tab === "earners" ? (
                      <>
                        <td className="px-4 py-3 text-right font-mono font-bold text-lg text-[var(--jd-secondary)]">
                          {fmt(row.earned)}
                        </td>
                        <td className="px-4 py-3 text-right hidden md:table-cell text-[var(--jd-ocean)]">
                          {row.runs}
                        </td>
                      </>
                    ) : (
                      <>
                        <td className="px-4 py-3 text-right font-mono font-bold text-lg text-[var(--jd-secondary)]">
                          {fmt(row.coins_awarded)}
                        </td>
                        <td className="px-4 py-3 text-right hidden md:table-cell text-[var(--jd-ocean)]">
                          {row.duration_ms
                            ? `${Math.floor(row.duration_ms / 1000)}s`
                            : "—"}
                        </td>
                      </>
                    )}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
};

const RankBadge = ({ rank }) => {
  if (rank === 1)
    return (
      <span className="inline-flex items-center gap-1 font-display font-bold text-lg text-[var(--jd-secondary)]">
        <Crown size={18} /> 1
      </span>
    );
  if (rank === 2)
    return (
      <span className="inline-flex items-center gap-1 font-display font-bold text-lg text-[var(--jd-primary)]">
        <Medal size={18} /> 2
      </span>
    );
  if (rank === 3)
    return (
      <span className="inline-flex items-center gap-1 font-display font-bold text-lg text-[var(--jd-accent)]">
        <Trophy size={18} /> 3
      </span>
    );
  return (
    <span className="font-display text-base text-[var(--jd-ocean)]">#{rank}</span>
  );
};

export default Leaderboard;
